import React from 'react';
import AddIcon from '@material-ui/icons/Add';
import Button from '../../Button';
import DataGridEngine from '../../../internal-components/data-grid/engine/DataGridEngine';
import styled from 'styled-components/macro';
import AddTaskDialogEngine from '../engine/AddTaskDialogEngine';
import EditTaskDialogEngine from '../engine/EditTaskDialogEngine';
import _Chip from '@material-ui/core/Chip';
import formatDate from '../../../utils/formatDate';
import {
  priorityToDisplayMap,
  statusToDisplayTextMap
} from '../engine/TasksTabEngine';
import Tooltip from '@material-ui/core/Tooltip';
import {
  isThisWeek,
  isToday,
  isTomorrow,
  isOverdue
} from '../../../utils/dateUtils';
import FilledClockIcon from '@material-ui/icons/WatchLater';
import OutlinedClockIcon from '@material-ui/icons/AccessTime';
import ViewOnlyBannerEngine from '../../engine/ViewOnlyBannerEngine';

const AddTaskButton = styled(Button)``;

const TABLE_COLUMN_DEFINITIONS = [
  { id: 'title', display: 'Task', width: '2fr' },
  { id: 'status', display: 'Status', width: '1fr' },
  { id: 'formattedDueDate', display: 'Due date', width: '2fr' },
  { id: 'formattedPriority', display: 'Priority', width: '2fr' },
  { id: 'assigneeDisplay', display: 'Assigned to', width: '2fr' },
  { id: 'authorDisplay', display: 'Created by', width: '2fr' }
];

const getHeaderStyle = columnIndex => {
  let style = 'font-size: 12px; weight: 600px; background-color: #fbfbfb;';
  if (columnIndex === 0) {
    style += 'border-radius: 8px 0 0 0';
  } else if (columnIndex === TABLE_COLUMN_DEFINITIONS.length - 1) {
    style += 'border-radius: 0 8px 0 0';
  } else {
    style += 'border-radius: 0';
  }
  return style;
};

const statusColours = {
  assigned: {
    background: '#e5edff',
    color: '#4c84ff'
  },
  inProgress: {
    background: '#FFF1E1',
    color: '#FE9928'
  },
  complete: {
    background: '#D3E7DC',
    color: '#0B773B'
  }
};

const Container = styled.div`
  padding: 0.5rem;
`;

const AddTaskContainer = styled.div`
  padding: 0 0 1rem;
`;

const UserDiv = styled.div`
  font-size: 12px;
  max-width: 237px;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const TitleDiv = styled.div`
  cursor: ${({ $viewOnly }) => ($viewOnly ? 'default' : 'pointer')};
`;

const EmailDiv = styled.div`
  color: #52535b;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const TimeWord = styled.span`
  && {
    line-height: 1;
    vertical-align: middle;
  }
`;
const TimeWordRed = styled.span`
  && {
    color: #df8e82;
    font-weight: ${({ $heavy }) => ($heavy ? '600' : '500')};
    line-height: 1;
    vertical-align: middle;
  }
`;
const TodayClock = styled(FilledClockIcon)`
  && {
    color: #df8e82;
    font-size: 15px;
    vertical-align: middle;
  }
`;
const TomorrowClock = styled(FilledClockIcon)`
  && {
    color: #df8e82;
    font-size: 15px;
    vertical-align: middle;
  }
`;
const ThisWeekClock = styled(OutlinedClockIcon)`
  && {
    color: #fdb259;
    font-size: 15px;
    vertical-align: middle;
  }
`;

export const StatusChip = styled(_Chip)`
  && {
    border-radius: 6px;
    background-color: #e5edff;
    color: #4c84ff;
    ${({ $status }) =>
      $status &&
      `color: ${
        statusColours[$status]?.color || '#4c84ff'
      }; background-color: ${statusColours[$status]?.background || '#e5edff'};`}
    font-size: 12px;
  }
`;

const NoRecordsCmp = () => {
  return <div>this is a separate thing</div>;
};

const TasksTabView = props => {
  const columns = TABLE_COLUMN_DEFINITIONS.map((colDef, i) => {
    const style = getHeaderStyle(i);
    return { ...colDef, customCSS: style };
  });
  const rows = props.tasks.map(one => {
    let formattedDueDate = one.dueDate
      ? formatDate(+one.dueDate, false, 'DD/MM/YYYY', true)
      : '';
    if (isToday(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TodayClock /> <TimeWordRed>Today</TimeWordRed>
        </div>
      );
    } else if (isTomorrow(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TomorrowClock /> <TimeWord>Tomorrow</TimeWord>
        </div>
      );
    } else if (isThisWeek(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <ThisWeekClock /> <TimeWord>This week</TimeWord>
        </div>
      );
    } else if (isOverdue(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TimeWordRed $heavy>! Overdue</TimeWordRed>
        </div>
      );
    }
    const formattedPriority = priorityToDisplayMap[one.priority];
    return {
      ...one,
      assigneeDisplay: (
        <UserDiv>
          <Tooltip
            title={
              <div>
                {one.assigneeName}
                <br />
                {one.assigneeEmailAddress}
              </div>
            }
          >
            <span>
              {one.assigneeName}
              <EmailDiv>{one.assigneeEmailAddress}</EmailDiv>
            </span>
          </Tooltip>
        </UserDiv>
      ),
      authorDisplay: (
        <UserDiv>
          <Tooltip
            title={
              <div>
                {one.authorName}
                <br />
                {one.authorEmailAddress}
              </div>
            }
          >
            <span>
              {one.authorName}
              <br />
              <EmailDiv>{one.authorEmailAddress}</EmailDiv>
            </span>
          </Tooltip>
        </UserDiv>
      ),
      title: (
        <TitleDiv onClick={() => props.clickedTask(one)}>{one.title}</TitleDiv>
      ),
      formattedDueDate,
      formattedPriority,
      status: (
        <StatusChip
          size="small"
          label={statusToDisplayTextMap[one.status]}
          $status={one.status}
          color="primary"
        />
      ),
      customCellCSS: 'font-size: 12px; justify-content: center;'
    };
  });
  return (
    <Container>
      {props.areTasksLoaded ? (
        <>
          {props.viewOnly ? (
            <ViewOnlyBannerEngine />
          ) : (
            <AddTaskContainer>
              <AddTaskButton onClick={props.clickedAdd}>
                <AddIcon />
                Add a task
              </AddTaskButton>
            </AddTaskContainer>
          )}
          <div>
            <DataGridEngine
              columns={columns}
              rows={rows}
              customCSS="border-radius: 8px; border: 1px solid #c9c9c9;"
              sort={props.sortObject}
              onSortChange={props.onSortChange}
              noRecordsText="No tasks added."
            />
          </div>
        </>
      ) : (
        ''
      )}
      <AddTaskDialogEngine
        open={props.isAddTaskDialogOpen}
        closeAddTaskDialog={props.closeAddTaskDialog}
      />
      <EditTaskDialogEngine
        key={props.taskBeingEdited?.id + props.refreshSeed}
        canReassign
        editedTaskCallback={props.editedTaskCallback}
        open={props.isEditTaskDialogOpen}
        taskBeingEdited={props.taskBeingEdited}
        closeEditTaskDialog={props.closeEditTaskDialog}
        viewOnly={props.viewOnly}
      />
    </Container>
  );
};

export default TasksTabView;
